import React, { useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { MIN_SELECTABLE_DATE, isDateRangeInvalid, todayIsoDate } from '../../utils/dateRange';

/** Props der DateRangePicker-Komponente. */
interface DateRangePickerProps {
  /** Von-Datum (YYYY-MM-DD) oder leerer String (= Server-Default). */
  readonly from: string;
  /** Bis-Datum (YYYY-MM-DD) oder leerer String (= Server-Default). */
  readonly to: string;
  /** Callback bei Aenderung des Von-Datums. */
  readonly onFromChange: (value: string) => void;
  /** Callback bei Aenderung des Bis-Datums. */
  readonly onToChange: (value: string) => void;
  /** Setzt beide Daten zurueck (leere Werte = Server-Defaults). */
  readonly onReset: () => void;
}

/**
 * Zeitraum-Auswahl fuer die Dashboard-Charts: zwei native date-Inputs
 * ("Von"/"Bis") und ein Zuruecksetzen-Button. Leere Werte bedeuten
 * Server-Defaults (die Query-Parameter werden dann weggelassen).
 *
 * Grenzen der Felder:
 * - beide Felder: min = MIN_SELECTABLE_DATE, max = heute
 * - zusaetzlich wechselseitig abgeleitet (Von <= Bis)
 *
 * Die Regel "Von darf nicht nach Bis liegen" kommt aus utils/dateRange,
 * damit Picker und DashboardPage dieselbe Definition benutzen.
 */
function DateRangePicker({
  from,
  to,
  onFromChange,
  onToChange,
  onReset,
}: DateRangePickerProps): React.JSX.Element {
  const invalid = isDateRangeInvalid(from, to);
  // Einmal pro Mount bestimmen: Der Tageswechsel waehrend einer Sitzung
  // rechtfertigt kein Neurendern der gesamten Filterzeile.
  const today = useMemo(() => todayIsoDate(), []);

  return (
    <div id="daterange-picker" className="d-flex flex-wrap align-items-end gap-2">
      <Form.Group id="daterange-from-group" controlId="daterange-from">
        <Form.Label id="daterange-from-label" className="small text-muted mb-1">
          Von
        </Form.Label>
        <Form.Control
          type="date"
          size="sm"
          value={from}
          min={MIN_SELECTABLE_DATE}
          max={to === '' || to > today ? today : to}
          isInvalid={invalid}
          onChange={(event) => onFromChange(event.target.value)}
        />
      </Form.Group>
      <Form.Group id="daterange-to-group" controlId="daterange-to">
        <Form.Label id="daterange-to-label" className="small text-muted mb-1">
          Bis
        </Form.Label>
        <Form.Control
          type="date"
          size="sm"
          value={to}
          min={from === '' || from < MIN_SELECTABLE_DATE ? MIN_SELECTABLE_DATE : from}
          max={today}
          isInvalid={invalid}
          onChange={(event) => onToChange(event.target.value)}
        />
      </Form.Group>
      <Button
        id="daterange-reset"
        variant="outline-secondary"
        size="sm"
        className="mb-1"
        disabled={from === '' && to === ''}
        onClick={onReset}
      >
        Zurücksetzen
      </Button>
      {invalid && (
        <div id="daterange-error" className="text-danger small w-100">
          Das Von-Datum darf nicht nach dem Bis-Datum liegen.
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
